import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuthFetch } from '../hooks/authFetch';
import { useRecentlyCreated } from '../hooks/useRecentlyCreated';

interface Device {
  device_id: string;
  nickname: string;
  model: string;
}

interface AssignDeviceModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  onAssign: (deviceId: string) => void;
  assignLoading: boolean;
  assignError: string | null;
  assignSuccess: string | null;
  assignedDeviceIds: string[];
}

const AssignDeviceModal: React.FC<AssignDeviceModalProps> = ({
  open,
  onClose,
  organizationId,
  onAssign,
  assignLoading,
  assignError,
  assignSuccess,
  assignedDeviceIds,
}) => {
  const [orgDevices, setOrgDevices] = useState<Device[]>([]);
  const [orgDevicesLoading, setOrgDevicesLoading] = useState(false);
  const authFetch = useAuthFetch();
  const { recentlyCreatedDevices } = useRecentlyCreated();

  useEffect(() => {
    if (open && organizationId) {
      setOrgDevicesLoading(true);
      authFetch(
        `${import.meta.env.VITE_SERVER_URL}/api/v1/devices/by-organization/${encodeURIComponent(organizationId)}`
      )
        .then(res => res.json())
        .then(setOrgDevices)
        .catch(() => setOrgDevices([]))
        .finally(() => setOrgDevicesLoading(false));
    }
  }, [open, organizationId, authFetch]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          minWidth: 500,
          maxWidth: 600,
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          borderRadius: 2,
        }}
      >
        <Typography variant='h6' gutterBottom>
          Assign Device to Senior
        </Typography>
        <Box
          sx={{
            mb: 2,
            maxHeight: 300,
            overflowY: 'auto',
            border: '1px solid #eee',
            borderRadius: 1,
            p: 1,
            bgcolor: '#fafbfc',
          }}
        >
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Devices in Organization:
          </Typography>
          {orgDevicesLoading ? (
            <CircularProgress size={20} sx={{ ml: 1 }} />
          ) : orgDevices.length === 0 ? (
            <Typography color='text.secondary'>No devices found.</Typography>
          ) : (
            (() => {
              // Sort devices: recently created in this session first, then by device_id
              const sortedDevices = orgDevices
                // Filter out devices that are already assigned to this senior
                .filter(dev => !assignedDeviceIds.includes(dev.device_id))
                .sort((a, b) => {
                  const aIsRecent = recentlyCreatedDevices.includes(
                    a.device_id
                  );
                  const bIsRecent = recentlyCreatedDevices.includes(
                    b.device_id
                  );

                  // If both or neither are recent, sort by device_id
                  if (aIsRecent === bIsRecent) {
                    return b.device_id.localeCompare(a.device_id);
                  }

                  // Recently created devices come first
                  return bIsRecent ? 1 : -1;
                });

              return sortedDevices.map(dev => {
                const isRecentlyCreated = recentlyCreatedDevices.includes(
                  dev.device_id
                );
                return (
                  <Box
                    key={dev.device_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 0.5,
                      pl: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: isRecentlyCreated ? '#e3f2fd' : 'transparent', // Light blue highlight for recently created
                      border: isRecentlyCreated
                        ? '1px solid #2196f3'
                        : '1px solid transparent',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: isRecentlyCreated ? '#bbdefb' : '#f5f5f5',
                        borderColor: isRecentlyCreated ? '#1976d2' : '#e0e0e0',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.25,
                        }}
                      >
                        <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                          <b>ID:</b> {dev.device_id}
                        </Typography>
                        {isRecentlyCreated && (
                          <Box
                            sx={{
                              bgcolor: '#4caf50',
                              color: 'white',
                              px: 0.5,
                              py: 0.125,
                              borderRadius: 0.5,
                              fontSize: '0.6rem',
                              fontWeight: 'bold',
                            }}
                          >
                            NEW
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant='body2'
                        sx={{ fontSize: '0.75rem', mb: 0.125 }}
                      >
                        <b>Nickname:</b> {dev.nickname}
                      </Typography>
                      <Typography variant='body2' sx={{ fontSize: '0.75rem' }}>
                        <b>Type:</b> {dev.model}
                      </Typography>
                    </Box>
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={() => onAssign(dev.device_id)}
                      disabled={assignLoading}
                      aria-label='assign device'
                      sx={{
                        width: 28,
                        height: 28,
                        border: '1px solid',
                        borderColor: 'primary.main',
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              });
            })()
          )}
        </Box>
        <Button onClick={onClose}>Close</Button>
        {assignError && (
          <Typography color='error' sx={{ mt: 2 }}>
            {assignError}
          </Typography>
        )}
        {assignSuccess && (
          <Typography color='success.main' sx={{ mt: 2 }}>
            {assignSuccess}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default AssignDeviceModal;
